import { NoughtsBoard } from '../components/NoughtsBoard.js';
import { useStore } from '../store.js';
import { PaperRoom, type PaperResult, type PaperSeat } from './PaperRoom.js';

/**
 * Noughts and crosses at the table.
 *
 * Thin on purpose: everything about *sitting with someone* lives in
 * `PaperRoom`, and everything about *the game* lives in `NoughtsBoard`. What is
 * left here is the translation between them — which is a handful of sentences,
 * and they are the interesting part, because the same board position means two
 * opposite things depending on one house rule.
 */
export function NoughtsRoom() {
  const view = useStore((s) => s.noughts);
  const lobby = useStore((s) => s.lobby);

  if (!view || !lobby) {
    return (
      <main className="game">
        <p className="game__loading">Setting up…</p>
      </main>
    );
  }

  const seats: PaperSeat[] = view.players.map((player) => ({
    id: player.id,
    name: player.name,
    isBot: player.isBot,
    connected: player.connected,
    isYou: player.id === view.yourPlayerId,
    isCurrent: player.id === view.currentPlayerId,
    badge: player.mark === 'X' ? '✕' : '◯',
    wins: lobby.members.find((member) => member.id === player.id)?.wins ?? 0,
  }));

  const finished = view.phase === 'finished';
  const yourTurn = view.currentPlayerId === view.yourPlayerId;
  const opponent = view.players.find((player) => player.id !== view.yourPlayerId);

  // The goal is worth keeping on screen rather than only in the log: under
  // misère the board looks exactly like a game you are trying to win.
  const goal = view.rules.misere
    ? `Avoid ${view.rules.winLength} in a row`
    : `${view.rules.winLength} in a row`;

  const status = finished
    ? goal
    : yourTurn
      ? `Your go · ${goal}`
      : `${opponent?.name ?? 'They'} to play · ${goal}`;

  return (
    <PaperRoom
      name="Noughts and Crosses"
      seats={seats}
      status={status}
      board={<NoughtsBoard view={view} />}
      finished={finished}
      result={finished ? describeResult(view.finishedOrder, view.yourPlayerId, view.rules.misere) : null}
    />
  );
}

/**
 * How the round ended, in the player's own terms.
 *
 * The misère wording is the whole reason this is a function rather than a
 * ternary. "You win" is true either way, but *why* is the opposite — you made
 * the line, or you avoided making it — and a result screen that cannot tell the
 * difference teaches the variant wrong.
 */
function describeResult(
  finishedOrder: string[],
  yourPlayerId: string | null,
  misere: boolean,
): PaperResult {
  if (finishedOrder.length === 0) {
    return {
      title: 'A draw',
      body: misere
        ? 'The grid filled and neither of you was forced into a line. Honours even.'
        : 'Every square taken and nothing lines up. Nobody gives an inch.',
      tone: 'drawn',
    };
  }

  const youWon = finishedOrder[0] === yourPlayerId;

  if (youWon) {
    return {
      title: 'You win! 🎉',
      body: misere
        ? 'They ran out of safe squares and had to make the line.'
        : 'Three of yours in a row, and nothing they could do about it.',
      tone: 'won',
    };
  }

  return {
    title: 'You lose',
    body: misere
      ? 'Nowhere safe left to go — you had to complete the line.'
      : 'They got there first. Line drawn, game over.',
    tone: 'lost',
  };
}
